import { api } from './api';

// Define a service using Spring Batch native monitoring endpoints
export const batchMonitoringApi = api.injectEndpoints({
  reducerPath: 'batchMonitoringApi',
  tagTypes: ['JobNames', 'JobInstances', 'JobExecutions', 'StepExecutions', 'JobStats'],
  endpoints: (builder) => ({
    // Get all job names (Spring Batch native)
    getAllJobNames: builder.query({
      query: () => ({
        url: `/api/administration/batchMonitoring/jobs`,
        method: 'GET'
      }),
      providesTags: ['JobNames']
    }),

    // Get job instances for a specific job
    getJobInstances: builder.query({
      query: ({ jobName, start = 0, count = 10 }) => ({
        url: `/api/administration/batchMonitoring/jobs/${encodeURIComponent(jobName)}/instances`,
        method: 'GET',
        params: { start, count }
      }),
      providesTags: (result, error, { jobName }) => [
        { type: 'JobInstances', jobName },
        'JobInstances'
      ]
    }),

    // Get job executions for a job instance
    getJobExecutions: builder.query({
      query: ({ instanceId }) => ({
        url: `/api/administration/batchMonitoring/instances/${instanceId}/executions`,
        method: 'GET'
      }),
      providesTags: (result, error, { instanceId }) => [
        { type: 'JobExecutions', instanceId },
        'JobExecutions'
      ]
    }),

    // Get job execution details
    getJobExecution: builder.query({
      query: ({ jobExecutionId }) => ({
        url: `/api/administration/batchMonitoring/executions/${jobExecutionId}`,
        method: 'GET'
      }),
      // Poll every 3 seconds for running jobs
      pollingInterval: (result) => {
        if (result?.data?.status === 'STARTED' || result?.data?.status === 'STARTING') {
          return 3000;
        }
        return 0; // No polling for completed jobs
      },
      providesTags: (result, error, { executionId }) => [{ type: 'JobExecutions', executionId }]
    }),

    // Get step executions for a job execution
    getStepExecutions: builder.query({
      query: ({ executionId }) => ({
        url: `/api/administration/batchMonitoring/executions/${executionId}/steps`,
        method: 'GET'
      }),
      // Poll every 3 seconds for step details of running jobs
      pollingInterval: 3000,
      providesTags: (result, error, { executionId }) => [{ type: 'StepExecutions', executionId }]
    }),

    getJobExecutionWithContext: builder.query({
      query: (jobExecutionId) => ({
        url: `/api/administration/batchMonitoring/executions/${jobExecutionId}/context`,
        method: 'GET'
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, jobExecutionId) => [{ type: 'DinaBatches', jobExecutionId }]
    }),

    getStepDetailsWithContext: builder.query({
      query: (jobExecutionId) => ({
        url: `/api/administration/batchMonitoring/executions/${jobExecutionId}/steps/context`,
        method: 'GET'
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, jobExecutionId) => [{ type: 'DinaBatches', jobExecutionId }]
    }),

    // Get running job executions for a specific job
    getRunningJobExecutions: builder.query({
      query: ({ jobName }) => ({
        url: `/api/administration/batchMonitoring/jobs/${encodeURIComponent(jobName)}/running`,
        method: 'GET'
      }),
      // Poll every 5 seconds for running jobs
      pollingInterval: 5000,
      providesTags: (result, error, { jobName }) => [
        { type: 'JobExecutions', jobName },
        'JobExecutions'
      ]
    }),

    // Get job parameters for a job execution
    getJobParameters: builder.query({
      query: ({ executionId }) => ({
        url: `/api/administration/batchMonitoring/executions/${executionId}/parameters`,
        method: 'GET'
      }),
      providesTags: (result, error, { executionId }) => [{ type: 'JobExecutions', executionId }]
    }),

    // Get job instance count
    getJobInstanceCount: builder.query({
      query: ({ jobName }) => ({
        url: `/api/administration/batchMonitoring/jobs/${encodeURIComponent(jobName)}/count`,
        method: 'GET'
      }),
      providesTags: (result, error, { jobName }) => [{ type: 'JobInstances', jobName }]
    }),

    getLatestExecutionForKnowledgeBase: builder.query({
      query: (knowledgeBaseCode) => ({
        url: `/api/administration/batchMonitoring/executions/knowledgeBase/${encodeURIComponent(knowledgeBaseCode)}/latest`,
        method: 'GET'
      }),
      providesTags: (result, error, knowledgeBaseCode) => [
        { type: 'JobExecutions', knowledgeBaseCode }
      ]
    }),

    // Custom metadata queries (if you implement the BatchMetadataService)
    getJobExecutionStats: builder.query({
      query: ({ jobName }) => ({
        url: `/api/administration/batchMonitoring/stats/job/${encodeURIComponent(jobName)}`,
        method: 'GET'
      }),
      // Poll every 30 seconds for stats
      pollingInterval: 30000,
      providesTags: (result, error, { jobName }) => [{ type: 'JobStats', jobName }]
    }),

    getRecentJobExecutions: builder.query({
      query: ({ limit = 20 }) => ({
        url: `/api/administration/batchMonitoring/stats/recent`,
        method: 'GET',
        params: { limit }
      }),
      // Poll every 10 seconds for recent executions
      pollingInterval: 10000,
      providesTags: ['JobExecutions']
    }),

    getJobExecutionDurations: builder.query({
      query: ({ jobName }) => ({
        url: `/api/administration/batchMonitoring/stats/durations/${encodeURIComponent(jobName)}`,
        method: 'GET'
      }),
      providesTags: (result, error, { jobName }) => [{ type: 'JobStats', jobName }]
    }),

    // Dashboard overview combining multiple data sources
    getDashboardData: builder.query({
      query: () => ({
        url: `/api/administration/batchMonitoring/dashboard`,
        method: 'GET'
      }),
      // Poll every 5 seconds for dashboard
      pollingInterval: 5000,
      providesTags: ['JobNames', 'JobExecutions', 'JobStats']
    }),

    // Get all running jobs across all job types
    getAllRunningJobs: builder.query({
      query: () => ({
        url: `/api/administration/batchMonitoring/running/all`,
        method: 'GET'
      }),
      // Poll every 5 seconds for all running jobs
      pollingInterval: 5000,
      providesTags: ['JobExecutions']
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetAllJobNamesQuery,
  useGetJobInstancesQuery,
  useGetJobExecutionsQuery,
  useGetJobExecutionQuery,
  useGetStepExecutionsQuery,
  useGetJobExecutionWithContextQuery,
  useGetStepDetailsWithContextQuery,
  useGetRunningJobExecutionsQuery,
  useGetJobParametersQuery,
  useGetJobInstanceCountQuery,
  useGetLatestExecutionForKnowledgeBaseQuery,
  useGetJobExecutionStatsQuery,
  useGetRecentJobExecutionsQuery,
  useGetJobExecutionDurationsQuery,
  useGetDashboardDataQuery,
  useGetAllRunningJobsQuery
} = batchMonitoringApi;
